import * as THREE from "three";
import { VFX } from "../../../../art/vfx.js";

/** The parts of art/vfx.js this file reaches into; they are internals the module does not type (see art/vfx.d.ts). */
interface SpriteLayer { n: number; geo: THREE.InstancedBufferGeometry; mat: THREE.ShaderMaterial; keys: string[]; end(cam: THREE.Camera): void }
interface VfxInternals { add: SpriteLayer; norm: SpriteLayer }

/** The warm-up bolt lives this long (seconds): a few frames, so it is drawn at least once. */
const WARM_LIFE = 0.1;
/** ...and sits this far below the ground, in case a driver rasterises a zero-width strip at all. */
const WARM_DEPTH = 50;

/**
 * art/vfx.js with its per-frame GPU uploads trimmed. The look is the module's own; only what reaches the GPU changes.
 *
 * The two sprite layers uploaded every instance attribute at full capacity (4000 sprites, ~300 KB a layer) every
 * frame, however few sprites were alive: ~0.6 MB of buffer uploads a frame in a battle. They now upload only the live
 * sprites' range, and nothing when a layer is empty.
 *
 * (This class used to re-implement the module's bolts and beams on pooled meshes as well. The art pass's vfx.js pools
 * them itself now — a fixed bolt pool whose strips are rewritten in place, and reused beam planes — so those overrides
 * are gone; test/vfxPool.test.ts still checks this class draws exactly what the module draws.)
 */
export class PooledVfx extends VFX {
  constructor(scene: THREE.Scene, camera: THREE.Camera, atlasTex: THREE.Texture, meta: ConstructorParameters<typeof VFX>[3]) {
    super(scene, camera, atlasTex, meta);
    const self = this as unknown as VfxInternals;
    for (const layer of [self.add, self.norm]) layer.end = (cam) => PooledVfx.endLayer(layer, cam);
  }

  /** Upload only the live sprites; three.js otherwise sends the whole 4000-sprite buffer. */
  private static endLayer(layer: SpriteLayer, cam: THREE.Camera): void {
    const g = layer.geo, n = layer.n;
    // An empty layer after an empty layer has nothing to send; instanceCount 0 already draws nothing.
    if (n > 0 || g.instanceCount > 0) {
      for (const name of layer.keys) {
        const a = g.attributes[name] as THREE.InstancedBufferAttribute;
        a.clearUpdateRanges();
        if (n > 0) { a.addUpdateRange(0, n * a.itemSize); a.needsUpdate = true; }
      }
    }
    g.instanceCount = n;
    const m = cam.matrixWorld.elements;
    (layer.mat.uniforms.camRight.value as THREE.Vector3).set(m[0], m[1], m[2]);
    (layer.mat.uniforms.camUp.value as THREE.Vector3).set(m[4], m[5], m[6]);
  }

  /**
   * Gets the bolt shader compiled now, while the level loads, instead of on the first tesla arc or fortress strike
   * mid-battle (a transparent double-sided material links two programs, front and back faces: a stall of tens of
   * milliseconds on a phone). It draws one zero-width — so invisible — bolt through the real frame, because only the
   * real pass (into the post-processing target) compiles the variant the battle will use.
   */
  warm(): void {
    const at = new THREE.Vector3(0, -WARM_DEPTH, 0);
    this.lightning(at, at.clone().setX(1), { life: WARM_LIFE, width: 0, glow: 0, core: 0 });
  }
}
